// frontend/src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PasswordInput from '../components/common/InputPassword';
import Icon from '../components/common/Icon';
import useAuth from '../hooks/useAuth';
import TwoFactorCodeStep from '../components/auth/TwoFactorCodeStep';
import TwoFactorSetupPanel from '../components/auth/TwoFactorSetupPanel';
import UnlockRequestBox from '../components/auth/UnlockRequestBox';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, user, verifyTwoFactor, completeSession } = useAuth();
  const [step, setStep] = useState('credentials'); // credentials | code | setup
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [locked, setLocked] = useState(false);
  const navigate = useNavigate();

  // اگر قبلاً وارد شده باشد، به داشبورد هدایت شود
  useEffect(() => {
    if (user) {
      if (user.role === 'ADMIN') {
        navigate('/admin/dashboard');
      } else {
        navigate('/club/dashboard');
      }
    }
  }, [user, navigate]);

  // بازیابی ایمیل ذخیره‌شده
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // اعتبارسنجی ساده
    if (!email || !password) {
      setError('لطفاً ایمیل و رمز عبور را وارد کنید.');
      setLoading(false);
      return;
    }

    try {
      const result = await login(email, password);

      if (result.step === 'CODE') {
        setTwoFactorToken(result.twoFactorToken);
        setStep('code');
        return;
      }
      if (result.step === 'SETUP') {
        setSetupToken(result.setupToken);
        setStep('setup');
        return;
      }

      finishLogin(result.user);
    } catch (err) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  const rememberEmail = () => {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
  };

  const finishLogin = (loggedInUser) => {
    rememberEmail();
    navigate(loggedInUser.role === 'ADMIN' ? '/admin/dashboard' : '/club/dashboard');
  };

  const handleLoginError = (err) => {
    const data = err?.response?.data;
    setLocked(Boolean(data?.errors?.some?.((e) => e.code === 'ACCOUNT_LOCKED')));
    setError(data?.message || 'ورود ناموفق بود. لطفاً اطلاعات خود را بررسی کنید.');
  };

  const handleCodeSubmit = async (code) => {
    setError('');
    setLoading(true);
    try {
      const result = await verifyTwoFactor(twoFactorToken, code);
      finishLogin(result.user);
    } catch (err) {
      const data = err?.response?.data;
      const expiredOrLocked = err?.response?.status === 423 || err?.response?.status === 429 || err?.response?.status === 401 && /منقضی/.test(data?.message || '');
      handleLoginError(err);
      if (expiredOrLocked) setStep('credentials');
    } finally {
      setLoading(false);
    }
  };

  // پس از فعال‌سازی اجباری: سرور همین‌جا نشست را هم ساخته است
  const handleSetupDone = (data) => {
    const loggedInUser = completeSession(data);
    finishLogin(loggedInUser);
  };

  const backToCredentials = () => {
    setStep('credentials');
    setError('');
    setPassword('');
  };

  return (
    <section className="relative flex min-h-screen flex-col overflow-hidden">
      {/* =========================================================
          تصویر پس‌زمینه
      ========================================================= */}
      <div className="absolute inset-0">
        <img
          src="/images/login-bg.png"
          alt="زمینه صفحه ورود"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#07150F]/90 via-[#07150F]/70 to-emerald-900/60" />
      </div>

      {/* =========================================================
          دکمه بازگشت به صفحه اصلی
      ========================================================= */}
      <Link
        to="/"
        className="absolute left-4 top-4 z-20 flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2 text-sm font-bold text-white/70 backdrop-blur-md transition-all hover:bg-white/20 hover:text-white md:left-8 md:top-8"
      >
        <Icon name="arrow-right" className="h-4 w-4" />
        بازگشت
      </Link>

      {/* =========================================================
          کادر ورود
      ========================================================= */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md animate-fade-in">
          {/* لوگو */}
          <div className="mb-8 text-center">
            <div className="animate-float mx-auto mb-4 grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-floodlight to-emerald-400 shadow-2xl shadow-emerald-500/40">
              <Icon name="trophy" className="h-10 w-10 text-ink" />
            </div>
            <h1 className="text-3xl font-black text-white">خوش آمدید</h1>
            <p className="mt-2 text-sm text-white/60">به سامانه مدیریت هیئت فوتبال شهرستان پاوه</p>
          </div>

          {step === 'code' && (
            <div className="glass-dark rounded-[2.5rem] px-8 py-10 md:px-10">
              <TwoFactorCodeStep onSubmit={handleCodeSubmit} onBack={backToCredentials} error={error} loading={loading} />
            </div>
          )}

          {step === 'setup' && (
            <div className="glass-dark rounded-[2.5rem] px-8 py-10 md:px-10">
              <div className="mb-4 rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs leading-6 text-amber-200">
                برای امنیت بیشتر، ورود دو مرحله‌ای برای حساب شما اجباری است. این مرحله فقط یک بار انجام می‌شود.
              </div>
              <TwoFactorSetupPanel dark setupToken={setupToken} onDone={handleSetupDone} onCancel={backToCredentials} />
            </div>
          )}

          {step === 'credentials' && (
          <form
            onSubmit={handleSubmit}
            className="glass-dark rounded-[2.5rem] px-8 py-10 md:px-10"
          >
            {/* =========================================================
                هدر فرم
            ========================================================= */}
            <div className="mb-6 text-center">
              <h2 className="text-xl font-black text-white">ورود به حساب کاربری</h2>
              <p className="mt-1 text-sm text-white/40">اطلاعات خود را وارد کنید</p>
            </div>

            {/* =========================================================
                ایمیل
            ========================================================= */}
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-bold text-white/80">ایمیل</label>
                <div className="relative">
                  <Input
                    placeholder="ایمیل خود را وارد کنید"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 w-full rounded-2xl border-white/10 bg-white/5 pr-4 text-white placeholder:text-white/30 backdrop-blur-sm transition-all focus:border-emerald-400 focus:bg-white/10"
                    required
                  />
                  <Icon
                    name="mail"
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-white/30"
                  />
                </div>
              </div>

              {/* =========================================================
                  رمز عبور
              ========================================================= */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-bold text-white/80">رمز عبور</label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-bold text-floodlight transition hover:text-floodlight-300"
                  >
                    رمز را فراموش کرده‌اید؟
                  </Link>
                </div>
                <PasswordInput
                  placeholder="رمز عبور خود را وارد کنید"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 w-full rounded-2xl border-white/10 bg-white/5 pr-4 text-white placeholder:text-white/30 backdrop-blur-sm transition-all focus:border-emerald-400 focus:bg-white/10"
                  required
                />
              </div>

              {/* =========================================================
                  مرا به خاطر بسپار
              ========================================================= */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-white/20 bg-white/10 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
                />
                <label htmlFor="rememberMe" className="text-sm text-white/60">
                  مرا به خاطر بسپار
                </label>
              </div>

              {/* =========================================================
                  خطا
              ========================================================= */}
              {error && (
                <div className="flex items-start gap-2 rounded-xl border border-red-400/20 bg-red-500/10 p-3 text-sm text-red-300">
                  <Icon name="alert-circle" className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {locked && <UnlockRequestBox email={email} />}

              {/* =========================================================
                  دکمه ورود
              ========================================================= */}
              <Button
                type="submit"
                variant="lime"
                size="lg"
                disabled={loading}
                className="h-12 w-full rounded-full bg-floodlight font-black text-ink shadow-lime transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/30 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    در حال ورود...
                  </span>
                ) : (
                  <>
                    ورود به سامانه
                    <Icon name="arrow-left" className="h-4 w-4" />
                  </>
                )}
              </Button>

              {/* =========================================================
                  لینک ثبت‌نام
              ========================================================= */}
              <div className="mt-4 text-center text-sm text-white/40">
                حساب کاربری ندارید؟{' '}
                <Link
                  to="/register"
                  className="font-bold text-floodlight transition hover:text-floodlight-300"
                >
                  ثبت‌ نام کنید
                </Link>
              </div>
            </div>
          </form>
          )}

          {/* =========================================================
              فوتر
          ========================================================= */}
          <div className="mt-6 text-center text-xs text-white/20">
            سامانه مدیریت هیئت فوتبال شهرستان پاوه
            <span className="mx-2">•</span>
            نسخه ۱.۰
          </div>
        </div>
      </div>
    </section>
  );
}